import React from 'react';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { ArrowRight, Send, TrendingUp, Users, BarChart3 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { EnquiryForm } from '../components/forms/EnquiryForm';

const EtudeRemunerationPage = () => {
  const sectors = [
    { name: 'Finance & Comptabilité', growth: '+4.2%', avgSalary: 'CHF 125,000' },
    { name: 'Technology', growth: '+6.8%', avgSalary: 'CHF 135,000' },
    { name: 'Services Financiers', growth: '+3.5%', avgSalary: 'CHF 145,000' },
    { name: 'Juridique', growth: '+2.9%', avgSalary: 'CHF 155,000' },
    { name: 'Ressources Humaines', growth: '+3.1%', avgSalary: 'CHF 110,000' }
  ];

  const highlights = [
    { icon: TrendingUp, title: '+4.5%', description: 'Augmentation moyenne des salaires' },
    { icon: Users, title: '85%', description: 'Des entreprises prévoient de recruter' },
    { icon: BarChart3, title: '3.2%', description: 'Taux de chômage en Suisse' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero */}
      <section className="relative bg-gray-900 text-white py-20">
        <div className="absolute inset-0">
          <ResponsiveImage loading="eager"
            src="https://images.unsplash.com/photo-1573649647330-b3a1375227fe?w=1600&q=80"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span data-testid="study-availability" className="text-white font-medium mb-4 block">Informations sur demande</span>
          <h1 className="text-4xl lg:text-5xl font-serif mb-6">Étude de rémunération 2026</h1>
          <p className="text-xl opacity-90 max-w-2xl mb-8">
            Découvrez les tendances salariales et de recrutement dans votre secteur d'activité en Suisse.
          </p>
          <a 
            href="#demande-etude"
            data-testid="study-request-link"
            className="inline-flex items-center gap-2 bg-[#E30022] text-white px-8 py-4 rounded hover:bg-[#c7001d] transition-colors font-medium group"
          >
            <Send className="h-5 w-5" />
            Demander l’étude
          </a>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="bg-[#E30022] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <div key={index} className="text-center">
                <item.icon className="h-10 w-10 mx-auto mb-4" />
                <p className="text-4xl font-bold mb-2">{item.title}</p>
                <p className="opacity-90">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-800 mb-4 text-center">Tendances par secteur</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Aperçu des évolutions salariales dans les principaux secteurs d'activité en Suisse.
          </p>

          <dl data-testid="study-sectors-mobile" className="md:hidden divide-y divide-gray-200">
            {sectors.map((sector, index) => (
              <div key={sector.name} className="py-6 first:pt-0 min-w-0">
                <dt data-testid={`study-mobile-sector-${index}-name`} className="font-semibold text-gray-800 mb-4 break-words">{sector.name}</dt>
                <dd className="space-y-3 text-sm">
                  <div className="flex flex-wrap justify-between gap-2"><span className="text-gray-600">Évolution 2025–2026</span><span data-testid={`study-mobile-sector-${index}-growth`} className="font-semibold text-green-700">{sector.growth}</span></div>
                  <div className="flex flex-wrap justify-between gap-2"><span className="text-gray-600">Salaire moyen</span><span data-testid={`study-mobile-sector-${index}-salary`} className="text-gray-800">{sector.avgSalary}</span></div>
                  <a href="#demande-etude" data-testid={`study-mobile-sector-${index}-link`} className="inline-block text-[#E30022] hover:underline font-medium">En savoir plus</a>
                </dd>
              </div>
            ))}
          </dl>
          <div className="hidden md:block">
            <table data-testid="study-sectors-desktop" className="w-full table-fixed">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-6 font-semibold text-gray-800">Secteur</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-800">Évolution 2025-2026</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-800">Salaire moyen</th>
                  <th className="text-left py-4 px-6"></th>
                </tr>
              </thead>
              <tbody>
                {sectors.map((sector, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td data-testid={`study-sector-${index}-name`} className="py-4 px-6 font-medium text-gray-800 break-words">{sector.name}</td>
                    <td className="py-4 px-6">
                      <span data-testid={`study-sector-${index}-growth`} className="text-green-600 font-semibold">{sector.growth}</span>
                    </td>
                    <td data-testid={`study-sector-${index}-salary`} className="py-4 px-6 text-gray-600">{sector.avgSalary}</td>
                    <td className="py-4 px-6">
                      <a href="#demande-etude" data-testid={`study-sector-${index}-link`} className="text-[#E30022] hover:underline text-sm font-medium">En savoir plus</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="demande-etude" className="bg-gray-50 py-20 scroll-mt-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12">
            <h2 data-testid="study-form-title" className="text-lg font-serif text-gray-800 mb-4 text-center">Demandez l’étude de rémunération</h2>
            <p data-testid="study-form-description" className="text-gray-600 text-center mb-8">
              Contactez notre équipe pour connaître les informations disponibles sur les rémunérations dans votre secteur.
            </p>

            <EnquiryForm kind="study" />
          </div>
        </div>
      </section>

      {/* Compare CTA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 text-white rounded-lg p-8">
              <h3 className="text-2xl font-serif mb-4">Vous êtes candidat ?</h3>
              <p className="text-gray-400 mb-6">Comparez votre salaire actuel avec les standards du marché suisse.</p>
              <a 
                href="#demande-etude"
                data-testid="study-candidate-request-link"
                className="inline-flex items-center gap-2 bg-[#E30022] text-white px-6 py-3 rounded hover:bg-[#c7001d] transition-colors font-medium group"
              >
                Comparer mon salaire
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <div className="bg-[#E30022] text-white rounded-lg p-8">
              <h3 className="text-2xl font-serif mb-4">Vous êtes employeur ?</h3>
              <p className="text-white/80 mb-6">Assurez-vous que vos packages de rémunération sont compétitifs.</p>
              <a 
                href="#demande-etude"
                data-testid="study-employer-request-link"
                className="inline-flex items-center gap-2 bg-white text-[#E30022] px-6 py-3 rounded hover:bg-gray-100 transition-colors font-medium group"
              >
                Comparer les salaires de mon équipe
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EtudeRemunerationPage;
